import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import type { ButtonProps } from "@mui/material"
import {
  Avatar,
  Button,
  Divider,
  Menu,
  MenuItem,
  styled,
  useMediaQuery,
} from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import useAuth from "../../utils/auth"

import { useFlags } from "launchdarkly-react-client-sdk"
import mixpanel from "mixpanel-browser"
import type { Path } from "react-router"
import { Link } from "react-router"
import { usePermissions } from "../../hooks/usePermissions"
import { ROUTES } from "../../utils/constants"
import { extraColors, theme } from "../../utils/theme"
import { NullDataToggle } from "./NullDataToggle"
import { SanityDatasetToggle } from "./SanityDatasetToggle"

const ButtonStyled = styled(Button)(({ theme: t }) => ({
  ...t.typography.body2,
  padding: "0px 8px",
}))

const NavLink: React.FC<
  ButtonProps & {
    to: Omit<Path, "hash">
    pathname?: string
    dataTestId?: string
  }
> = ({ to, children, dataTestId }) => (
  <Button
    variant="text"
    color="inherit"
    component={Link}
    to={to}
    disableRipple={true}
    sx={{
      ...theme.typography.body2,
      "&:hover": {
        backgroundColor: "transparent",
      },
    }}
    data-testid={dataTestId}
  >
    {children}
  </Button>
)

const UserContainer = styled("div")(() => ({
  display: "flex",
  "@media print": {
    display: "none",
  },
}))

const UserButton = styled(Button)({
  ...theme.typography.body1Bold,
  justifyContent: "end",
  display: "flex",
  background: extraColors.dark10,
  color: extraColors.white,
  borderRadius: 100,
  boxShadow: "inset 0px 1px 3px rgba(0, 0, 0, 0.12)",
  padding: "0px 9px",
  "&:hover, &:focus, &:active": {
    background: extraColors.dark10,
    color: extraColors.white,
    boxShadow: "inset 0px 1px 3px rgba(0, 0, 0, 0.12)",
    textDecoration: "none",
  },
})

const MenuItemStyled = styled(MenuItem)({
  justifyContent: "center",
})

export const UserNavigation: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { showCmsDatasetToggle, showAlertCuration } = useFlags()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { logout, user } = useAuth()
  const client = useQueryClient()
  const permissions = usePermissions()
  const handleClose = () => setAnchorEl(null)

  if (!user) return null

  return (
    <UserContainer data-testid="user-dropdown" className={className}>
      {isMobile ? (
        <Avatar
          style={{
            width: 36,
            height: 36,
            cursor: "pointer",
          }}
          alt={user.name}
          src={user.picture}
          slotProps={{
            img: { referrerPolicy: "no-referrer" },
          }}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget)
          }}
        />
      ) : (
        <UserButton
          variant="text"
          size="large"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget)
          }}
          startIcon={
            <Avatar
              style={{
                width: 23,
                height: 23,
              }}
              alt={user.name}
              src={user.picture}
              slotProps={{ img: { referrerPolicy: "no-referrer" } }}
            />
          }
          endIcon={<KeyboardArrowDownIcon />}
          disableRipple={true}
          data-testid="header-user"
        >
          {user.name}
        </UserButton>
      )}

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 35,
          horizontal: 23,
        }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItemStyled onClick={handleClose}>
          <NavLink to={{ pathname: ROUTES.PREFERENCES, search: "" }}>
            Settings
          </NavLink>
        </MenuItemStyled>

        {(permissions.organizations.read ||
          permissions.currentUser.organization.users.read) && (
          <MenuItemStyled onClick={handleClose}>
            <NavLink to={{ pathname: ROUTES.ORGANIZATIONS, search: "" }}>
              Admin
            </NavLink>
          </MenuItemStyled>
        )}
        <MenuItemStyled onClick={handleClose}>
          <NavLink to={{ pathname: ROUTES.DOCUMENTATION, search: "" }}>
            Vector API Documentation
          </NavLink>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleClose}>
          <NavLink to={{ pathname: ROUTES.FAQ, search: "" }}>FAQ</NavLink>
        </MenuItemStyled>

        <MenuItemStyled>
          <a
            href="mailto:help@phcglobal.com?subject=Help%20with%20PHC%20Global"
            style={{ textDecoration: "none" }}
          >
            <ButtonStyled variant="text" color="inherit" disableRipple={true}>
              Help
            </ButtonStyled>
          </a>
        </MenuItemStyled>

        {showCmsDatasetToggle && (
          <MenuItemStyled>
            <SanityDatasetToggle />
          </MenuItemStyled>
        )}

        {showAlertCuration && (
          <div>
            <Divider sx={{ my: 1, borderColor: extraColors.translucent8 }} />
            <MenuItemStyled>
              <NullDataToggle />
            </MenuItemStyled>
          </div>
        )}

        <MenuItemStyled
          onClick={() => {
            handleClose()
            mixpanel.reset()
            client.clear()
            void logout({ returnTo: window.location.origin })
          }}
        >
          <ButtonStyled variant="text" color="inherit" disableRipple={true}>
            Log out
          </ButtonStyled>
        </MenuItemStyled>
      </Menu>
    </UserContainer>
  )
}
